import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number} */
  #stickyUpdateToken = 0;

  /** @type {MutationObserver | null} */
  #stickyObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #stickyObserverTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#cancelStickyStateUpdate);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#cancelStickyStateUpdate);
    this.#cancelStickyStateUpdate();
    this.#historyAbortController?.abort();
  }

  /**
   * The history entry has to be pushed before the drawer opens, not on DialogOpenEvent:
   * pushState records the current scroll position onto the entry we leave behind, and by the
   * time that event fires super.showDialog() has already parked <body> at position:fixed, so
   * the entry would remember 0 and the browser would throw the page to the top when the
   * drawer closes. Running first, the entry remembers where the customer actually is and the
   * browser restores it for us.
   *
   * Compared against `false` rather than negated: with a missing or detached ref, super throws
   * and the drawer never opens, so an entry pushed here would leave the back button silently
   * eating a navigation instead of closing anything.
   */
  showDialog() {
    if (this.refs.dialog?.open === false) this.#openHistoryEntry();
    super.showDialog();
  }

  #openHistoryEntry = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleDialogOpen = () => {
    this.#scheduleStickyStateUpdate();
  };

  /**
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // Remeasure after cart-items morph. Empty→filled ATC opens the drawer before summary exists;
    // morph.js also preserves cart-summary-sticky, so open-time measure alone can stick on false.
    this.#scheduleStickyStateUpdate();

    if (!this.#isDrawerUpsellAdd(event)) return;

    // Double rAF: on translated languages the cart-items morph lands after a section re-fetch
    // (hydration morph preserves scroll), so the later top-jump still wins.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#scrollDrawerToTop();
      });
    });
  };

  /**
   * Upsell ATC lives in the drawer; detect via event target before morph detaches it.
   * @param {Event} event
   * @returns {boolean}
   */
  #isDrawerUpsellAdd(event) {
    if (event.detail?.data?.didError) return false;
    if (!this.refs.dialog?.open) return false;

    const source = event.target;
    if (!(source instanceof HTMLElement)) return false;

    return source.hasAttribute('data-cart-drawer-upsell');
  }

  /**
   * Scroll drawer overflow containers to top after upsell add.
   * Sticky summary → items scrolls; unsticky → content scrolls. Both carry `data-cart-drawer-scroll`.
   */
  #scrollDrawerToTop() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open) return;

    const scrollers = dialog.querySelectorAll('[data-cart-drawer-scroll]');
    if (!scrollers.length) return;

    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';

    for (const scroller of scrollers) {
      if (!(scroller instanceof HTMLElement)) continue;
      if (scroller.scrollTop === 0) continue;

      scroller.scrollTo({ top: 0, behavior });
    }
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #cancelStickyStateUpdate = () => {
    this.#stickyUpdateToken += 1;
    this.#disconnectStickyObserver();
  };

  #disconnectStickyObserver() {
    if (this.#stickyObserver) {
      this.#stickyObserver.disconnect();
      this.#stickyObserver = null;
    }

    if (this.#stickyObserverTimeout !== null) {
      clearTimeout(this.#stickyObserverTimeout);
      this.#stickyObserverTimeout = null;
    }
  }

  /**
   * Measure sticky after layout (post sync morph via double rAF).
   * If summary is still missing, observe DOM until morph injects it (async section render fallback).
   */
  #scheduleStickyStateUpdate() {
    const token = ++this.#stickyUpdateToken;
    this.#disconnectStickyObserver();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (token !== this.#stickyUpdateToken) return;

        this.#updateStickyState();

        const { dialog } = /** @type {Refs} */ (this.refs);
        if (!dialog?.open) return;
        if (dialog.querySelector('[data-cart-drawer-summary]')) return;

        this.#observeSummaryAppearance(token);
      });
    });
  }

  /**
   * @param {number} token
   */
  #observeSummaryAppearance(token) {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    this.#stickyObserver = new MutationObserver(() => {
      if (token !== this.#stickyUpdateToken || !dialog.open) {
        this.#disconnectStickyObserver();
        return;
      }

      if (!dialog.querySelector('[data-cart-drawer-summary]')) return;

      this.#updateStickyState();
      this.#disconnectStickyObserver();
    });

    this.#stickyObserver.observe(dialog, { childList: true, subtree: true });

    // Empty cart never grows a summary — stop watching.
    this.#stickyObserverTimeout = setTimeout(() => {
      if (token !== this.#stickyUpdateToken) return;
      this.#disconnectStickyObserver();
      this.#updateStickyState();
    }, 2000);
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('[data-cart-drawer-content]');
    const summary = dialog.querySelector('[data-cart-drawer-summary]');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
